import { traverse } from "./traverse.js";
const upgradeFlow = (flow) => {
  switch (flow) {
    case "application":
      return "clientCredentials";
    case "accessCode":
      return "authorizationCode";
    case "implicit":
      return "implicit";
    case "password":
      return "password";
    default:
      return flow;
  }
};
function upgradeFromTwoToThree(originalSpecification) {
  let specification = originalSpecification;
  if (specification !== null && typeof specification === "object" && typeof specification.swagger === "string" && specification.swagger?.startsWith("2.0")) {
    specification.openapi = "3.0.4";
    delete specification.swagger;
  } else {
    return specification;
  }
  if (specification.host) {
    const schemes = Array.isArray(specification.schemes) && specification.schemes?.length ? specification.schemes : ["http"];
    specification.servers = schemes.map((scheme) => ({
      url: `${scheme}://${specification.host}${specification.basePath ?? ""}`
    }));
    delete specification.basePath;
    delete specification.schemes;
    delete specification.host;
  } else if (specification.basePath) {
    specification.servers = [{ url: specification.basePath }];
    delete specification.basePath;
  }
  if (specification.definitions) {
    specification.components = Object.assign({}, specification.components, {
      schemas: specification.definitions
    });
    delete specification.definitions;
    specification = traverse(specification, (schema) => {
      if (typeof schema.$ref === "string" && schema.$ref.startsWith("#/definitions/")) {
        schema.$ref = schema.$ref.replace(/^#\/definitions\//, "#/components/schemas/");
      }
      return schema;
    });
  }
  specification = traverse(specification, (schema) => {
    if (schema.type === "file") {
      schema.type = "string";
      schema.format = "binary";
    }
    return schema;
  });
  if (Object.hasOwn(specification, "parameters")) {
    specification.components ??= {};
    const params = {};
    const bodyParams = {};
    for (const [name, param] of Object.entries(specification.parameters ?? {})) {
      if (param.in === "body") {
        bodyParams[name] = migrateBodyParameter(
          param,
          specification.consumes ?? ["application/json"]
        );
      } else if (param.in === "formData") {
        bodyParams[name] = migrateFormDataParameter(param);
      } else {
        params[name] = transformParameterObject(param);
      }
    }
    if (Object.keys(params).length > 0) {
      ;
      specification.components.parameters = params;
    }
    if (Object.keys(bodyParams).length > 0) {
      ;
      specification.components.requestBodies = bodyParams;
    }
    delete specification.parameters;
  }
  if (typeof specification.paths === "object") {
    for (const path in specification.paths) {
      if (Object.hasOwn(specification.paths, path)) {
        const pathItem = specification.paths[path];
        let requestBodyObject;
        for (const methodOrParameters in pathItem) {
          if (methodOrParameters === "parameters" && Object.hasOwn(pathItem, methodOrParameters)) {
            const pathItemParameters = migrateParameters(
              pathItem.parameters,
              specification.consumes ?? ["application/json"]
            );
            pathItem.parameters = pathItemParameters.parameters;
            requestBodyObject = pathItemParameters.requestBody;
          } else if (Object.hasOwn(pathItem, methodOrParameters)) {
            const operationItem = pathItem[methodOrParameters];
            if (requestBodyObject) {
              operationItem.requestBody = requestBodyObject;
            }
            if (operationItem.parameters) {
              const migrationResult = migrateParameters(
                operationItem.parameters,
                operationItem.consumes ?? specification.consumes ?? ["application/json"]
              );
              operationItem.parameters = migrationResult.parameters;
              if (migrationResult.requestBody) {
                operationItem.requestBody = migrationResult.requestBody;
              }
              delete operationItem.consumes;
            }
            if (operationItem.responses) {
              for (const response in operationItem.responses) {
                if (Object.hasOwn(operationItem.responses, response)) {
                  const responseItem = operationItem.responses[response];
                  if (responseItem.headers) {
                    responseItem.headers = Object.entries(responseItem.headers).reduce((acc, [name, header]) => {
                      return {
                        [name]: transformParameterObject(header),
                        ...acc
                      };
                    }, {});
                  }
                  if (responseItem.schema) {
                    const produces = specification.produces ?? operationItem.produces ?? ["application/json"];
                    if (typeof responseItem.content !== "object") {
                      responseItem.content = {};
                    }
                    for (const type of produces) {
                      responseItem.content[type] = {
                        schema: responseItem.schema
                      };
                    }
                    delete responseItem.schema;
                  }
                }
              }
            }
            delete operationItem.produces;
            if (operationItem.parameters?.length === 0) {
              delete operationItem.parameters;
            }
          }
        }
      }
    }
  }
  if (specification.securityDefinitions) {
    if (typeof specification.components !== "object") {
      specification.components = {};
    }
    specification.components = specification.components;
    Object.assign(specification.components, { securitySchemes: {} });
    for (const [key, securityScheme] of Object.entries(specification.securityDefinitions)) {
      if (typeof securityScheme === "object") {
        if ("type" in securityScheme && securityScheme.type === "oauth2") {
          const { flow, authorizationUrl, tokenUrl, scopes } = securityScheme;
          Object.assign(specification.components.securitySchemes, {
            [key]: {
              type: "oauth2",
              flows: {
                [upgradeFlow(flow)]: Object.assign(
                  {},
                  authorizationUrl && { authorizationUrl },
                  tokenUrl && { tokenUrl },
                  scopes && { scopes }
                )
              }
            }
          });
        } else if ("type" in securityScheme && securityScheme.type === "basic") {
          Object.assign(specification.components.securitySchemes, {
            [key]: {
              type: "http",
              scheme: "basic"
            }
          });
        } else {
          Object.assign(specification.components.securitySchemes, {
            [key]: securityScheme
          });
        }
      }
    }
    delete specification.securityDefinitions;
  }
  delete specification.consumes;
  delete specification.produces;
  return specification;
}
function transformItemsObject(obj) {
  const schemaProperties = [
    "type",
    "format",
    "items",
    "maximum",
    "exclusiveMaximum",
    "minimum",
    "exclusiveMinimum",
    "maxLength",
    "minLength",
    "pattern",
    "maxItems",
    "minItems",
    "uniqueItems",
    "enum",
    "multipleOf"
  ];
  return schemaProperties.reduce((acc, property) => {
    if (Object.hasOwn(obj, property)) {
      acc[property] = obj[property];
      delete obj[property];
    }
    return acc;
  }, {});
}
function transformParameterObject(parameter) {
  const serializationStyle = getParameterSerializationStyle(parameter);
  const schema = transformItemsObject(parameter);
  delete parameter.collectionFormat;
  delete parameter.default;
  return {
    schema,
    ...serializationStyle,
    ...parameter
  };
}
const querySerialization = {
  ssv: {
    style: "spaceDelimited",
    explode: false
  },
  pipes: {
    style: "pipeDelimited",
    explode: false
  },
  multi: {
    style: "form",
    explode: true
  },
  csv: {
    style: "form",
    explode: false
  },
  tsv: {}
};
const pathAndHeaderSerialization = {
  ssv: {},
  pipes: {},
  multi: {},
  csv: {
    style: "simple",
    explode: false
  },
  tsv: {}
};
const serializationStyles = {
  header: pathAndHeaderSerialization,
  query: querySerialization,
  path: pathAndHeaderSerialization
};
function getParameterSerializationStyle(parameter) {
  if (parameter.type !== "array" || !(parameter.in === "query" || parameter.in === "path" || parameter.in === "header")) {
    return {};
  }
  const collectionFormat = parameter.collectionFormat ?? "csv";
  return serializationStyles[parameter.in][collectionFormat];
}
function migrateBodyParameter(bodyParameter, consumes) {
  delete bodyParameter.name;
  delete bodyParameter.in;
  const { schema, ...requestBody } = bodyParameter;
  const requestBodyObject = {
    content: {},
    ...requestBody
  };
  for (const type of consumes) {
    requestBodyObject.content[type] = {
      schema
    };
  }
  return requestBodyObject;
}
function migrateFormDataParameter(parameters) {
  const requestBodyObject = {
    content: {}
  };
  requestBodyObject.content["application/x-www-form-urlencoded"] = {
    schema: {
      type: "object",
      properties: {},
      required: []
      // Initialize required array
    }
  };
  for (const param of parameters) {
    requestBodyObject.content["application/x-www-form-urlencoded"].schema.properties[param.name] = {
      type: param.type,
      description: param.description
    };
    if (param.required) {
      requestBodyObject.content["application/x-www-form-urlencoded"].schema.required.push(param.name);
    }
  }
  return requestBodyObject;
}
function migrateParameters(parameters, consumes) {
  const result = {
    parameters: parameters.filter((parameter) => !(parameter.in === "body" || parameter.in === "formData")).map((parameter) => transformParameterObject(parameter))
  };
  const bodyParameter = structuredClone(
    parameters.find((parameter) => parameter.in === "body") ?? {}
  );
  if (bodyParameter && Object.keys(bodyParameter).length) {
    result.requestBody = migrateBodyParameter(bodyParameter, consumes);
  }
  const formDataParameters = parameters.filter((parameter) => parameter.in === "formData");
  if (formDataParameters.length > 0) {
    const requestBodyObject = migrateFormDataParameter(formDataParameters);
    if (typeof result.requestBody !== "object") {
      result.requestBody = requestBodyObject;
    } else {
      result.requestBody = {
        ...result.requestBody,
        content: {
          ...result.requestBody.content,
          ...requestBodyObject.content
        }
      };
    }
    if (typeof result.requestBody !== "object") {
      result.requestBody = {
        content: {}
      };
    }
  }
  return result;
}
export {
  upgradeFromTwoToThree
};
//# sourceMappingURL=upgrade-from-two-to-three.js.map
